/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { X, Download, AlertCircle, CheckCircle, Loader, Settings } from 'lucide-react';
import { SkillShopService } from '../../services/skillShopService';
import { useApp } from '../../contexts/AppContext';
import { Link } from 'wouter';

interface JiraUserStoryImportDialogProps {
  onClose: () => void;
  onImportComplete: () => void;
}

/**
 * Dialog for importing user stories and epics from JIRA.
 * Uses JIRA configuration from Settings page (via AppContext).
 */
export function JiraUserStoryImportDialog({ onClose, onImportComplete }: JiraUserStoryImportDialogProps) {
  const { jiraConfig, isConfigured, activeProduct } = useApp();
  const [step, setStep] = useState<'config' | 'preview' | 'importing' | 'complete'>('config');
  const [maxResults, setMaxResults] = useState(100);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [skillShopService] = useState(() => new SkillShopService());

  const loadPreview = async () => {
    if (!jiraConfig) return;
    
    setLoadingPreview(true);
    setError(null);
    
    const result = await skillShopService.previewJiraUserStoryImport({
      url: jiraConfig.url,
      username: jiraConfig.username,
      apiToken: jiraConfig.apiToken,
      projectKey: jiraConfig.projectKey,
      epicIssueType: jiraConfig.epicIssueType || 'Epic',
      issueType: jiraConfig.issueType || 'Story'
    }, { maxResults });
    
    if (result.success) {
      setPreview(result.data);
      setStep('preview');
    } else {
      setError(result.error || 'Failed to load preview');
    }
    
    setLoadingPreview(false);
  };

  const performImport = async () => {
    if (!jiraConfig) return;
    
    setImporting(true);
    setError(null);
    setStep('importing');
    
    const result = await skillShopService.importJiraUserStories({
      url: jiraConfig.url,
      username: jiraConfig.username,
      apiToken: jiraConfig.apiToken,
      projectKey: jiraConfig.projectKey,
      epicIssueType: jiraConfig.epicIssueType || 'Epic',
      issueType: jiraConfig.issueType || 'Story'
    }, {
      maxResults,
      updateExisting,
      repoUrl: activeProduct?.repositoryUrl ?? undefined,
      repoPath: activeProduct?.repoPath ?? undefined
    });
    
    if (result.success) {
      setImportResult(result.data);
      setStep('complete');
    } else {
      setError(result.error || 'Import failed');
      setStep('preview');
    }
    
    setImporting(false);
  };

  const handleComplete = () => {
    onImportComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Download className="w-5 h-5" />
            Import User Stories from JIRA
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Not Configured Message */}
        {!isConfigured && step === 'config' && (
          <div className="p-6">
            <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-900 mb-2">JIRA Not Configured</h3>
                  <p className="text-sm text-amber-800 mb-4">
                    To import user stories from JIRA, you need to configure your JIRA connection in Settings first.
                    This includes your JIRA URL, username, API token, project key, and issue types.
                  </p>
                  <Link href="/settings" onClick={onClose}>
                    <button className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Go to Settings
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Active Product Warning */}
        {isConfigured && !activeProduct?.repositoryUrl && step === 'config' && (
          <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">No Active Product</p>
              <p className="text-sm text-amber-700 mt-1">
                Please select a product with a repository on the Products page before importing.
                User stories will be saved to the active product's repository.
              </p>
            </div>
          </div>
        )}

        {/* Configuration Step - Only show import options when JIRA is configured */}
        {isConfigured && step === 'config' && (
          <div className="p-6">
            {/* Show current JIRA connection info */}
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">Connected to JIRA</span>
              </div>
              <div className="text-sm text-green-800 space-y-1">
                <p><span className="font-medium">Instance:</span> {jiraConfig?.url}</p>
                <p><span className="font-medium">Project:</span> {jiraConfig?.projectKey}</p>
                <p><span className="font-medium">User:</span> {jiraConfig?.username}</p>
                <p><span className="font-medium">Epic Type:</span> {jiraConfig?.epicIssueType || 'Epic'}</p>
                <p><span className="font-medium">Story Type:</span> {jiraConfig?.issueType || 'Story'}</p>
              </div>
              <p className="text-xs text-green-700 mt-2">
                <Link href="/settings" className="underline hover:no-underline">Change in Settings</Link>
              </p>
            </div>

            {/* Show target repository */}
            {activeProduct?.repositoryUrl && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm text-blue-800">
                  <p><span className="font-medium">Target Product:</span> {activeProduct.title}</p>
                  <p><span className="font-medium">Repository:</span> {activeProduct.repositoryUrl}</p>
                  {activeProduct.repoPath && (
                    <p><span className="font-medium">Path:</span> {activeProduct.repoPath}</p>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Results
                </label>
                <input
                  type="number"
                  value={maxResults}
                  onChange={(e) => setMaxResults(parseInt(e.target.value) || 100)}
                  min={1}
                  max={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="updateExisting"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="updateExisting" className="text-sm text-gray-700">
                  Update existing stories if found
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={loadPreview}
                disabled={loadingPreview}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loadingPreview && <Loader className="w-4 h-4 animate-spin" />}
                {loadingPreview ? 'Loading Preview...' : 'Preview Import'}
              </button>
            </div>
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && preview && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Import Preview
              </h3>
              <p className="text-sm text-gray-600">
                Found {preview.epics?.length || 0} epic{(preview.epics?.length || 0) !== 1 ? 's' : ''} and {preview.stories?.length || 0} user stor{(preview.stories?.length || 0) !== 1 ? 'ies' : 'y'} to import
              </p>
            </div>

            {/* Epics Table */}
            {preview.epics?.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Epics</h4>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stories</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {preview.epics.map((epic: any) => (
                        <tr key={epic.key} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{epic.key}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{epic.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{epic.storyCount || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Stories Table */}
            {preview.stories?.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">User Stories</h4>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Summary</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Epic</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {preview.stories.map((story: any) => (
                        <tr key={story.key} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{story.key}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{story.summary}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{story.epicKey || '—'}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{story.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('config')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={performImport}
                disabled={importing}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {importing && <Loader className="w-4 h-4 animate-spin" />}
                {importing ? 'Importing...' : `Import ${(preview.epics?.length || 0) + (preview.stories?.length || 0)} Items`}
              </button>
            </div>
          </div>
        )}

        {/* Importing Step */}
        {step === 'importing' && (
          <div className="p-6 text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Importing user stories...</p>
            <p className="text-sm text-gray-600 mt-2">This may take a few moments</p>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && importResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Import Complete!</h3>
              <p className="text-gray-600">
                Successfully imported {importResult.successful || 0} items
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{importResult.created || 0}</p>
                <p className="text-sm text-green-900">Created</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{importResult.updated || 0}</p>
                <p className="text-sm text-blue-900">Updated</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-600">{importResult.skipped || 0}</p>
                <p className="text-sm text-gray-900">Skipped</p>
              </div>
            </div>

            {importResult.errors?.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Errors:</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {importResult.errors.map((err: string, index: number) => (
                      <li key={index}>• {err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <button
              onClick={handleComplete}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

